import { useContext, useEffect, useState, useMemo } from 'react'
import { t } from '@lingui/core/macro'
import DeleteIcon from '@mui/icons-material/Delete'
import PeopleIcon from '@mui/icons-material/People'
import Button from '@mui/material/Button'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import IconButton from '@mui/material/IconButton'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import { useQuery } from '@tanstack/react-query'
import { useDispatch } from 'react-redux'
import { CvsAutoComplete } from '@components'
import { formatSelectedOptions } from '@components/select/cvs-autocomplete'
import { modalContext } from '@contexts'
import { getUserIdsDraftWasSharedWith } from '@global/api'
import { api } from 'api'
import { showSnackbar } from 'functions'

const DialogSelect = ({ draft, updateSelected }) => {
  const [availableUsers, setAvailableUsers] = useState([])
  const [selected, setSelected] = useState([])
  const [loading, setLoading] = useState(true)

  const { data: userIdsDraftWasSharedWith = [], isLoading } = useQuery(
    ['users-shared-draft', draft.id],
    ({ signal }) => getUserIdsDraftWasSharedWith({ draftId: draft.id, signal }),
  )

  useEffect(() => {
    if (userIdsDraftWasSharedWith.length) setSelected(userIdsDraftWasSharedWith)
  }, [userIdsDraftWasSharedWith])

  useEffect(() => {
    api
      .get(`/api/v1/account/groups/template/users/${draft.template_id}/${draft.id}`)
      .then(response => {
        const available = response.data.map(usr => ({ label: usr.name, value: usr.id }))
        setAvailableUsers(available)
      })
      .catch(error => {
        console.error(error)
      })
      .finally(() => {
        setLoading(false)
      })
  }, [])

  const onSelected = users => {
    setSelected(users)
    updateSelected(users)
  }

  return (
    <CvsAutoComplete
      label={t`Select...`}
      options={availableUsers}
      selected={formatSelectedOptions(true, selected, availableUsers)}
      required
      isMulti={true}
      isLoading={isLoading || loading}
      onSelected={onSelected}
    />
  )
}

export const DeleteShareIcons = ({ draft, invalidateQueryCache }) => {
  const dispatch = useDispatch()
  const [shareOpen, setShareOpen] = useState(false)
  const ModalContext = useContext(modalContext)
  const [shareUsers, setShareUsers] = useState([])

  const deleteDraft = draft => {
    api
      .put('/api/v1/account/dataset/draft/delete', { draft_id: draft.id })
      .then(() => {
        invalidateQueryCache()
        dispatch(
          showSnackbar({
            message: t`Draft deleted successfully`,
            type: 'SUCCESS',
            duration: 5000,
          }),
        )
      })
      .catch(() => {
        dispatch(
          showSnackbar({
            message: t`Draft failed to delete`,
            type: 'ERROR',
            duration: 5000,
          }),
        )
      })
  }

  const updateSelected = useMemo(
    () => selected => {
      setShareUsers(selected)
    },
    [],
  )

  const addUsersToDraft = () => {
    api
      .post('/api/v1/account/dataset/draft/addUsers', {
        users: shareUsers,
        draft_id: draft.id,
      })
      .then(() => {
        dispatch(
          showSnackbar({
            message: t`Draft shared successfully`,
            type: 'SUCCESS',
            duration: 5000,
          }),
        )
      })
      .catch(() => {
        dispatch(
          showSnackbar({
            message: t`Failed to share draft`,
            type: 'ERROR',
            duration: 5000,
          }),
        )
      })
    setShareOpen(false)
  }

  function deleteDraftModal(draftId) {
    ModalContext.displayModal({
      title: t`Are you sure you want to delete this draft?`,
      description: t`This action can't be undone and any work saved in this draft will be lost.`,
      confirm: () => () => {
        deleteDraft(draftId)
      },
    })
  }

  return (
    <>
      <div style={{ display: 'flex' }}>
        <IconButton
          style={{ padding: '4px' }}
          onClick={e => {
            e.stopPropagation()
            deleteDraftModal(draft, e)
          }}
          size="large"
        >
          <Tooltip title={t`Delete draft`}>
            <DeleteIcon />
          </Tooltip>
        </IconButton>
        <IconButton
          style={{ padding: '4px' }}
          onClick={e => {
            // shareDraftModal(draft, e)
            setShareOpen(true)
            e.stopPropagation()
          }}
          size="large"
        >
          <Tooltip title={t`Share draft`}>
            <PeopleIcon />
          </Tooltip>
        </IconButton>
      </div>
      <Dialog
        onClick={e => {
          e.stopPropagation()
        }}
        open={shareOpen}
        onClose={() => {
          setShareOpen(false)
        }}
      >
        <DialogTitle>{t`Please select users to share this draft with`}</DialogTitle>
        <Typography variant="caption" style={{ padding: '0 24px' }}>
          {t`You can only share drafts with users you share a group with.`}
        </Typography>
        <DialogContent>
          <DialogSelect draft={draft} updateSelected={updateSelected} />
        </DialogContent>
        <DialogActions>
          <div style={{ display: 'flex' }}>
            <Button
              color="primary"
              onClick={() => {
                setShareOpen(false)
              }}
            >
              {t`Cancel`}
            </Button>
            <Button
              color="primary"
              variant="outlined"
              autoFocus
              onClick={() => {
                addUsersToDraft()
              }}
            >
              {t`Share`}
            </Button>
          </div>
        </DialogActions>
      </Dialog>
    </>
  )
}
